import { SKETCHPLAN_PRICING_COPY } from '@rehub/pricing';

export const APP_NAME = "Sketchplan.rehub.studio";
export const APP_VERSION = '1.0.0';

/** Marketing copy for landing page */
export const SKETCHPLAN_TAGLINE = `${SKETCHPLAN_PRICING_COPY.headline}. ${SKETCHPLAN_PRICING_COPY.supporting}`;
export const SKETCHPLAN_FEATURES = [
  { title: 'Drawn by people', description: 'A person redraws your sketch into a clean, CAD-style floor plan. This is not an automatic trace of your lines.' },
  { title: 'Standard 48-hour delivery', description: 'Standard delivery is 48 hours from a confirmed paid order. Choose Express for 24 hours, with a 50% surcharge.' },
  { title: 'One round of amendments', description: SKETCHPLAN_PRICING_COPY.amendmentLong },
  { title: 'Simple sketch upload', description: 'Upload a photo, scan or PDF of your hand-drawn sketch and measurements. No CAD software required.' },
  { title: 'Portal-ready files', description: 'Receive a high-resolution JPG, PNG or PDF for listings and brochures. Editable CAD files such as DWG are not included.' },
  { title: 'Residential and commercial', description: `${SKETCHPLAN_PRICING_COPY.gbpFromResidential} ${SKETCHPLAN_PRICING_COPY.gbpFromCommercial} ${SKETCHPLAN_PRICING_COPY.commercialSurcharge} Multiple floors are included in the price for the total area.` },
];

export const MAX_FILE_SIZE_MB = 20;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

// Default prompts
export const DEFAULT_DECLUTTER_PROMPT = "Remove the objects marked with the red mask. Fill in the background naturally to make the room look clean, spacious, and decluttered suitable for real estate photography. Ensure lighting and perspective match perfectly.";

export const AUTO_DECLUTTER_PROMPT = "Automatically identify and remove all clutter, personal items, loose objects, and mess from this room. Keep major furniture, structural elements, and fixtures intact. STRICTLY PRESERVE: walls, ceilings, flooring, permanent fixtures, paint colors, and architectural features. Only remove movable items. The result should be a clean, tidy, and spacious room suitable for a professional real estate listing.";

export const GARDEN_DECLUTTER_PROMPT = "Automatically identify and remove ONLY movable man-made clutter such as gardening tools, hoses, buckets, toys, trash, and stray equipment. STRICTLY PRESERVE all plants, trees, bushes, flowers, and the lawn condition. Do not remove weeds or alter any physical landscaping features. Only remove temporary items that can be picked up.";

export const DRIVEWAY_DECLUTTER_PROMPT = "Automatically identify and remove garbage bins, wheelie bins, trash cans, parked cars, vehicles, oil stains, and weeds from the driveway and front exterior. Ensure the driveway looks clean, empty, and spacious while maintaining the paving texture.";

export const BINS_ONLY_PROMPT = "STRICTLY ONLY remove visible garbage bins, wheelie bins, rubbish bins, and trash cans from this image. DO NOT remove cars, vehicles, or any other objects. DO NOT alter the driveway surface, paving, or any landscaping. ONLY remove bins and nothing else.";

export const CARS_ONLY_PROMPT = "STRICTLY ONLY remove parked cars and vehicles from the driveway in this image. Replace them with clean, empty driveway that matches the existing surface texture and material EXACTLY. DO NOT alter the driveway paving, texture, color, or pattern. DO NOT remove bins, landscaping, or any other objects. Preserve the exact driveway material - match bricks, concrete, gravel, or tarmac precisely.";

export const BED_DECLUTTER_PROMPT = "Identify the bed. Without moving the bed, changing its orientation, or altering the frame, simply flatten and smooth the bed sheets and duvet. Remove wrinkles to create a crisp, professional look. STRICTLY preserve the original bed position and bedding patterns.";
export const BRIGHTEN_ENHANCE_PROMPT = "Enhance this image for professional real estate photography. Brighten dark areas, improve overall lighting balance, boost colors to look vibrant but natural, increase clarity and sharpness. DO NOT remove, add, or move any objects. Keep the composition exactly the same. The result should look like a professionally lit and color-graded real estate photo.";

// Sky Replacement Prompts
export const BLUE_SKY_PROMPT = "Replace the sky in this exterior photo with a beautiful clear blue sky with a few wispy white clouds. Maintain realistic lighting on the building and landscape that matches the new bright sky. Preserve all architectural details, trees, and landscaping. The result should look natural and professionally photographed.";

export const SUNSET_SKY_PROMPT = "Replace the sky in this exterior photo with a stunning {{SUNSET_TYPE}}. Adjust the lighting on the building and landscape to realistically match the warm golden hour tones from the new sky. Preserve all architectural details, trees, and landscaping. The result should look natural and dramatically beautiful.";

export const WINDOW_SKY_PROMPT = "Replace the view through all windows in this interior photo with a beautiful clear blue sky with soft white clouds. Only modify what is visible THROUGH the windows - do not alter the window frames, curtains, blinds, or any interior elements. Ensure the new sky view looks natural and adds brightness to the room.";

export const SUNSET_TYPES = [
  { id: 'golden-hour', label: 'Golden Hour', description: 'warm golden hour sunset with soft orange and yellow hues' },
  { id: 'dramatic-orange', label: 'Dramatic Orange', description: 'dramatic orange and red sunset with vivid colors' },
  { id: 'pink-purple', label: 'Pink & Purple', description: 'beautiful pink and purple sunset with pastel tones' },
  { id: 'fire-sky', label: 'Fire Sky', description: 'intense fiery red and orange sunset sky' },
  { id: 'soft-pastel', label: 'Soft Pastel', description: 'soft pastel sunset with gentle pink, peach, and lavender tones' },
  { id: 'twilight-blue', label: 'Twilight Blue', description: 'twilight sky with deep blue and purple gradients transitioning from sunset' },
];

export const ADD_FLOWERS_PROMPT = "Identify a suitable coffee table, dining table, or kitchen counter in the scene and realistically place a vase of {{FLOWERS}} on it to stage the room. Ensure the lighting, shadows, and perspective match the scene perfectly.";

export const MANUAL_FLOWERS_PROMPT = "Realistically place a vase of {{FLOWERS}} exactly in the area marked with the red mask. Adapt the flowers to the perspective and lighting of the surface at that location. Do not remove the surface, place the vase ON TOP of it. The mask represents the position and size of the vase.";

export const PIN_FLOWERS_PROMPT = "Identify the area marked with the red dot. Realistically place a vase of {{FLOWERS}} exactly at this spot. The red dot represents the center of the base of the vase on the surface. Ensure realistic shadows and perspective relative to the table or counter at that precise location.";

export const FLOWER_TYPES = [
  { id: 'white-tulips', label: 'White Tulips', description: 'fresh white tulips' },
  { id: 'white-orchids', label: 'White Orchids', description: 'elegant white orchids' },
  { id: 'mixed-wildflowers', label: 'Wildflowers', description: 'vibrant mixed wildflowers' },
  { id: 'red-roses', label: 'Red Roses', description: 'classic red roses' },
  { id: 'eucalyptus', label: 'Eucalyptus', description: 'modern eucalyptus greenery' },
  { id: 'peonies', label: 'Pink Peonies', description: 'soft pink peonies' },
  { id: 'sunflowers', label: 'Sunflowers', description: 'bright yellow sunflowers' },
  { id: 'blue-hydrangeas', label: 'Blue Hydrangeas', description: 'voluminous blue hydrangeas' },
  { id: 'white-lilies', label: 'White Lilies', description: 'elegant white lilies' },
  { id: 'pampas-grass', label: 'Pampas Grass', description: 'boho pampas grass' },
  { id: 'succulents', label: 'Succulent Bowl', description: 'modern succulent arrangement' },
  { id: 'cherry-blossoms', label: 'Cherry Blossoms', description: 'tall cherry blossom branches' },
  { id: 'lavender', label: 'Lavender', description: 'fragrant lavender stems' },
  { id: 'dahlias', label: 'Dahlias', description: 'colorful dahlia blooms' },
  { id: 'ranunculus', label: 'Ranunculus', description: 'delicate ranunculus flowers' },
  { id: 'protea', label: 'Protea', description: 'exotic protea arrangement' },
  { id: 'daisies', label: 'Daisies', description: 'cheerful white daisies' },
  { id: 'carnations', label: 'Carnations', description: 'fluffy carnation blooms' },
  { id: 'baby-breath', label: "Baby's Breath", description: "delicate baby's breath" },
  { id: 'magnolias', label: 'Magnolias', description: 'elegant magnolia branches' },
];

export const PLANT_TYPES = [
  { id: 'fiddle-leaf', label: 'Fiddle Leaf Fig', description: 'a tall fiddle leaf fig tree in a stylish pot' },
  { id: 'monstera', label: 'Monstera', description: 'a lush monstera deliciosa plant in a modern planter' },
  { id: 'snake-plant', label: 'Snake Plant', description: 'a tall snake plant (sansevieria) in a minimalist pot' },
  { id: 'pothos', label: 'Pothos', description: 'a trailing pothos plant in a hanging or shelf planter' },
  { id: 'rubber-plant', label: 'Rubber Plant', description: 'a glossy rubber plant in a ceramic pot' },
  { id: 'peace-lily', label: 'Peace Lily', description: 'a flowering peace lily in an elegant planter' },
  { id: 'bird-of-paradise', label: 'Bird of Paradise', description: 'a dramatic bird of paradise plant in a floor pot' },
  { id: 'zz-plant', label: 'ZZ Plant', description: 'a modern ZZ plant with glossy leaves in a contemporary pot' },
  { id: 'palm', label: 'Areca Palm', description: 'a feathery areca palm in a woven basket planter' },
  { id: 'fern', label: 'Boston Fern', description: 'a lush boston fern in a hanging or pedestal planter' },
  { id: 'olive-tree', label: 'Olive Tree', description: 'a small decorative olive tree in a terracotta pot' },
  { id: 'cactus', label: 'Cactus Collection', description: 'a collection of small cacti in decorative pots' },
  { id: 'philodendron', label: 'Philodendron', description: 'a trailing philodendron in a modern planter' },
  { id: 'aloe-vera', label: 'Aloe Vera', description: 'a healthy aloe vera plant in a minimalist pot' },
  { id: 'bamboo', label: 'Lucky Bamboo', description: 'elegant lucky bamboo stalks in a glass vase' },
];

export const ADD_PLANTS_PROMPT = "Identify a suitable corner, floor space, or shelf in the scene and realistically place {{PLANT}} to stage the room. Ensure the plant size is proportionate to the space, and the lighting, shadows, and perspective match the scene perfectly.";

export const MANUAL_PLANTS_PROMPT = "Realistically place {{PLANT}} exactly in the area marked with the red mask. Adapt the plant to the perspective and lighting of that location. The mask represents the position and approximate size of the plant. Ensure realistic shadows.";

export const PIN_PLANTS_PROMPT = "Identify the area marked with the red dot. Realistically place {{PLANT}} exactly at this spot. The red dot represents the center of the base of the plant pot on the floor or surface. Ensure realistic shadows and perspective relative to the space at that precise location.";

// ============================================================================
// ARCHITECT SYSTEM PROMPT - For extracting vector floor plan data from sketches
// ============================================================================

export const ARCHITECT_SYSTEM_PROMPT = `You are an expert architectural floor plan analyzer. Your task is to extract vector data from hand-drawn floor plan sketches.

CRITICAL: This is a TRACING task. You must trace every line you see in the sketch, even if it's rough or hand-drawn.

Analyze the provided floor plan image and extract:
1. ALL walls as line segments with pixel coordinates
2. ALL room labels with their positions  
3. Any dimension measurements visible

COORDINATE RULES:
- Use PIXEL coordinates (0,0 is top-left)
- X increases to the right, Y increases downward
- All coordinates must be within the image bounds
- Scale your coordinates to match the provided image dimensions

Return ONLY a valid JSON object with this EXACT structure:
{
  "width": <image width in pixels>,
  "height": <image height in pixels>,
  "walls": [
    {
      "id": "w1",
      "x1": <start x pixel>,
      "y1": <start y pixel>,
      "x2": <end x pixel>,
      "y2": <end y pixel>,
      "thickness": 6
    }
  ],
  "labels": [
    {
      "id": "l1",
      "x": <x pixel position>,
      "y": <y pixel position>,
      "text": "<room name>",
      "fontSize": 16
    }
  ]
}

EXTRACTION RULES:
1. TRACE EVERY LINE you see - even rough pencil lines
2. Break complex shapes into individual line segments (rectangles = 4 lines)
3. Each wall segment should be a straight line from point A to point B
4. For curved lines, approximate with multiple short straight segments
5. Include interior walls, exterior walls, and any partition lines
6. Extract room names from any handwritten text you can read
7. If you see a rectangle, that's 4 walls (top, bottom, left, right)
8. Connect walls at corners (endpoints should be close or matching)

IMPORTANT: Even a simple sketch of a rectangular room should produce at least 4 wall segments. Always output walls for any lines you detect.

ONLY return the JSON object, no other text.`;

// ============================================================================
// PRICING CONSTANTS - Floor plan pricing based on sq ft and property type
// ============================================================================

export const PRICING = {
  // Base cost for residential properties up to 1,000 sq ft
  BASE_CREDITS: 25,
  BASE_SQFT_THRESHOLD: 1000,
  
  // Additional credits for each 500 sq ft over the base (rounded up)
  ADDITIONAL_SQFT_INCREMENT: 500,
  ADDITIONAL_CREDITS_PER_INCREMENT: 5,
  
  // Commercial properties get 50% uplift over residential, rounded to nearest 5 credits
  COMMERCIAL_UPLIFT_PERCENT: 50,
  COMMERCIAL_ROUNDING: 5,
  
  // Properties over this threshold require a custom (bespoke) quote
  CUSTOM_QUOTE_THRESHOLD: 10000,
  
  // Express turnaround multiplier (50% more credits)
  EXPRESS_MULTIPLIER: 1.5,
};

/**
 * Calculate credit cost for a floor plan based on sq ft and property type
 * @param sqFt - Total square footage
 * @param propertyType - 'residential' or 'commercial'
 * @param isExpress - Whether express turnaround is selected
 * @returns Credit cost or null if custom quote required
 */
export const calculateFloorPlanCredits = (
  sqFt: number,
  propertyType: 'residential' | 'commercial',
  isExpress: boolean = false
): number | null => {
  // Properties over threshold require custom quote
  if (sqFt > PRICING.CUSTOM_QUOTE_THRESHOLD) {
    return null;
  }
  
  // Base cost: 25 credits for up to 1,000 sq ft
  let credits = PRICING.BASE_CREDITS;
  
  // Additional 5 credits for each 500 sq ft over the base (rounded up)
  if (sqFt > PRICING.BASE_SQFT_THRESHOLD) {
    const additionalSqFt = sqFt - PRICING.BASE_SQFT_THRESHOLD;
    const additionalIncrements = Math.ceil(additionalSqFt / PRICING.ADDITIONAL_SQFT_INCREMENT);
    credits += additionalIncrements * PRICING.ADDITIONAL_CREDITS_PER_INCREMENT;
  }
  
  // Commercial properties: apply 50% uplift, round to nearest 5
  if (propertyType === 'commercial') {
    credits = credits * (1 + PRICING.COMMERCIAL_UPLIFT_PERCENT / 100);
    credits = Math.round(credits / PRICING.COMMERCIAL_ROUNDING) * PRICING.COMMERCIAL_ROUNDING;
  }
  
  // Express turnaround: 50% more credits
  if (isExpress) {
    credits = Math.ceil(credits * PRICING.EXPRESS_MULTIPLIER);
  }
  
  return credits;
};

// Per-credit price when buying exact credits (no pack discount) — used for £/$ display and checkout
export const CREDIT_PRICE_USD = 1.15;
export const CREDIT_PRICE_GBP = 0.85;

/** Format credit cost as USD and GBP amounts for display */
export const formatCreditCostCurrency = (credits: number): { usd: number; gbp: number } => ({
  usd: credits * CREDIT_PRICE_USD,
  gbp: credits * CREDIT_PRICE_GBP,
});

// Canvas settings
export const DEFAULT_BRUSH_SIZE = 40;
export const MIN_BRUSH_SIZE = 10;
export const MAX_BRUSH_SIZE = 100;
export const MASK_COLOR = "rgba(255, 0, 0, 0.5)"; // Semi-transparent red for display
export const COMPOSITE_MASK_COLOR = "rgb(255, 0, 0)"; // Solid red for AI processing
export const MAX_PROCESSING_DIMENSION = 4096; // 4K resolution limit